import { motion } from "framer-motion";
import { Check, Sparkle } from "@phosphor-icons/react";
import { SectionHeader } from "@/components/site/SectionHeader";
import { fadeUp, staggerParent, viewport } from "@/lib/motion";

const tiers = [
  {
    name: "Free",
    price: "€0",
    period: "forever",
    desc: "Get started with core crypto intelligence.",
    features: [
      "7 AI crypto analyses per month",
      "2 presale intelligence analyses per month",
      "Basic platform access",
      "Portfolio & watchlist",
    ],
    featured: false,
  },
  {
    name: "Premium",
    price: "€19.99",
    period: "per month",
    yearly: "or €180 / year",
    desc: "Expanded, premium capabilities for serious users.",
    features: [
      "Expanded / unlimited premium capabilities",
      "Full AI crypto analysis access",
      "Advanced presale intelligence",
      "AI smart monitoring & alerts",
      "Priority platform features",
    ],
    featured: true,
  },
];

export const PricingSection = ({ withHeader = true }) => (
  <section
    id="pricing"
    className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-4 py-24 sm:px-6 md:py-32"
    data-testid="pricing-section"
  >
    {withHeader && (
      <SectionHeader
        label="Pricing"
        title="Simple, transparent plans"
        desc="Start free and upgrade when you're ready. Plans are managed and purchased inside the mobile app."
        align="center"
      />
    )}
    <motion.div
      variants={staggerParent}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2"
    >
      {tiers.map((t) => (
        <motion.div
          key={t.name}
          variants={fadeUp}
          data-testid={`pricing-tier-${t.name.toLowerCase()}`}
          className={`card-hover-glow relative overflow-hidden rounded-3xl border p-8 ${
            t.featured
              ? "border-teal-500/40 bg-gradient-to-b from-teal-500/10 to-transparent"
              : "border-white/8 bg-white/[0.02]"
          }`}
        >
          {t.featured && (
            <span className="absolute right-6 top-6 inline-flex items-center gap-1 rounded-full bg-teal-500 px-3 py-1 text-[11px] font-bold text-midnight">
              <Sparkle size={12} weight="fill" /> Most capable
            </span>
          )}
          <h3 className="font-display text-lg font-bold text-white">{t.name}</h3>
          <p className="mt-2 text-sm text-white/50">{t.desc}</p>
          <div className="mt-6 flex items-baseline gap-2">
            <span className="font-display text-4xl font-extrabold text-white">
              {t.price}
            </span>
            <span className="text-sm text-white/45">/ {t.period}</span>
          </div>
          {t.yearly && (
            <p className="mt-1 text-xs font-medium text-teal-400">{t.yearly}</p>
          )}
          <ul className="mt-7 space-y-3.5">
            {t.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/70">
                <span
                  className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${
                    t.featured ? "bg-teal-500/20 text-teal-400" : "bg-white/8 text-white/60"
                  }`}
                >
                  <Check size={12} weight="bold" />
                </span>
                {f}
              </li>
            ))}
          </ul>
          <div
            data-testid={`pricing-cta-${t.name.toLowerCase()}`}
            className={`mt-8 w-full rounded-full px-6 py-3 text-center text-sm font-semibold ${
              t.featured
                ? "bg-teal-500 text-midnight"
                : "border border-white/12 text-white/80"
            }`}
          >
            Available in the app
          </div>
        </motion.div>
      ))}
    </motion.div>
  </section>
);
